import { MenuItem, Select, Switch, Typography } from '@material-ui/core'
import { ArrowForward } from '@material-ui/icons'
import {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { CardExtraAttribute } from 'shared'
import { ExtraAttributesValuesAndImagesMapInterface } from 'utils/constants'

// informationsRelatedToCurrentAttribute structure:
// {attribute: 'isFoil', icons: Array(1)}
// {
// // attribute: "isFoil"
// // icons: Array(1)
// // // 0:  {img: '/static/media/foilIcon.6ee054f0.png', value: 'yes'}
// }
// length: 1

export interface EditExtraAttributesCellInterface {
  locationName: string
  currentAttribute: CardExtraAttribute
  oldValue: any
  setNewAttribute: React.Dispatch<React.SetStateAction<any>>
  disabled: boolean
  gameInformations: ExtraAttributesValuesAndImagesMapInterface
}
export interface EditExtraAttributesCellRef {
  resetValue: () => void
}

const EditExtraAttributesCell: ForwardRefRenderFunction<
  EditExtraAttributesCellRef,
  EditExtraAttributesCellInterface
> = (
  { gameInformations, locationName, currentAttribute, oldValue, setNewAttribute, disabled },
  ref
) => {
  const [hasEditedValue, setHasEditedValue] = useState(false)
  const [newEditedValue, setNewEditedValue] = useState(oldValue)
  const { t } = useTranslation()

  useImperativeHandle(ref, () => ({
    resetValue() {
      setNewEditedValue(oldValue)
      setNewAttribute(oldValue)
    },
  }))

  const informationsRelatedToCurrentAttribute: ExtraAttributesValuesAndImagesMapInterface['cardExtraAttribute'][0] =
    gameInformations.cardExtraAttribute
      .filter((item) => item.attribute !== CardExtraAttribute.Playset)!
      .find((attribute) => attribute.attribute === currentAttribute)!

  const convertValueFromSelectionToStateValue = (value: any) => {
    if (value === 'true' || value === true) return true
    if (value === 'false' || value === false) return false
    return value
  }

  const convertValueFromStateToLabelToDisplay = (value: any) => {
    if (value === true) return t('common.yes')
    if (value === false) return t('common.no')
    return value
  }

  const handleChanges = (e: any) => {
    const value = convertValueFromSelectionToStateValue(e)
    setNewEditedValue(value)
    setNewAttribute(value)
  }

  useEffect(() => {
    if (oldValue !== newEditedValue) setHasEditedValue(true)
    else setHasEditedValue(false)
  }, [oldValue, newEditedValue])

  const toggleSwitchComponent = (attribute: any) => {
    return hasEditedValue ? (
      <div className="d-flex flex-row justify-content-center align-items-center">
        <Typography>{convertValueFromStateToLabelToDisplay(oldValue)}</Typography>
        <ArrowForward className="mx-1" />
        <Switch
          data-testid={`${currentAttribute}-${locationName}`}
          onChange={(e) => {
            handleChanges(e.target.checked)
          }}
          checked={newEditedValue}
        />
      </div>
    ) : (
      <Select
        className="locations-panel"
        data-testid={`${attribute.attribute}-${locationName}`}
        value={newEditedValue}
        onChange={(e) => {
          handleChanges(e.target.value)
        }}
        disabled={disabled}
      >
        <MenuItem value="true">{t('common.yes')}</MenuItem>
        <MenuItem value="false">{t('common.no')}</MenuItem>
      </Select>
    )
  }

  const chooseIconsComponents = (
    attribute: ExtraAttributesValuesAndImagesMapInterface['cardExtraAttribute'][0]
  ) => {
    return hasEditedValue ? (
      <div className="d-flex flex-row justify-content-center align-items-center">
        <Typography>{convertValueFromStateToLabelToDisplay(oldValue)}</Typography>
        <ArrowForward className="mx-1" />
        <Select
          data-testid={`${currentAttribute}-${locationName}`}
          value={newEditedValue}
          onChange={(e) => handleChanges(e.target.value)}
        >
          <MenuItem value="false">NO</MenuItem>

          {attribute.icons.map((image: any) => {
            return (
              <MenuItem key={`${locationName}-${image.img}`} value={image.value}>
                <img src={image.img} style={{ width: '18px', height: '18px' }} />
              </MenuItem>
            )
          })}
        </Select>
      </div>
    ) : (
      <Select
        className="locations-panel"
        data-testid={`${attribute.attribute}-${locationName}`}
        value={newEditedValue}
        onChange={(e) => {
          handleChanges(e.target.value)
        }}
        disabled={disabled}
      >
        <MenuItem value="false">NO</MenuItem>
        {attribute.icons.map((image: any) => {
          return (
            <MenuItem
              key={`${locationName}-${currentAttribute}-${image.value}`}
              value={image.value}
              data-testid={`${locationName}-${currentAttribute}-${image.value}`}
            >
              <img src={image.img} style={{ width: '18px', height: '18px' }} />
            </MenuItem>
          )
        })}
      </Select>
    )
  }

  //if I have only true-false value I show component with toggle switch. else I show components where choose values
  const displayCellAccordingCurrentAttribute = (
    informationsRelatedToCurrentAttribute: ExtraAttributesValuesAndImagesMapInterface['cardExtraAttribute'][0]
  ) => {
    if (!informationsRelatedToCurrentAttribute) return null
    if (informationsRelatedToCurrentAttribute.icons.length > 1)
      return chooseIconsComponents(informationsRelatedToCurrentAttribute)
    return toggleSwitchComponent(informationsRelatedToCurrentAttribute)
  }

  return <>{displayCellAccordingCurrentAttribute(informationsRelatedToCurrentAttribute)}</>
}

export default forwardRef(EditExtraAttributesCell)
